import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
// Aliased: the outside-click effect below listens on `document` and wants the
// DOM `MouseEvent`, while the row handler gets React's synthetic one.
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useAppDispatch } from 'app/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { ahuBoardApi, usePatchAhuTunnelMutation } from 'app/services/ahuBoardApi';
import {
  useGetAhuCasingQuery,
  useGetAhuFloorQuery,
  usePatchAhuCasingMutation,
  usePatchAhuFloorMutation,
} from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardTunnel, IAhuBoardUnit, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { refetchHybridLugPlacements } from '../../boardActions';
import { validateTunnel } from '../../validation/validationController';
import PaginatedSelect from '../../forms/PaginatedSelect';
import { formatLegacyNumber } from '../../forms/formUtils';
import { cmsWidthValues, cmsHeightValues } from '../../forms/cmsDoc';

/**
 * Phase 6e1 — tunnel cabinet rows (§7.3.1, tab.ahu.tunnel.cabinet.js +
 * tab.ahu.cabinet.tmpl, markup faithful — debug class `2222` dropped per
 * [NEG]#21). W/H selects + readonly L + 4 paginated selects (casing outer
 * skin / skin finish → PATCH casing; floor under-insulation / under-liner →
 * PATCH floor).
 *
 * §19.2 tunnel-save cascade reproduced on PATCH success:
 *   width/height → ahu.validateTunnel + `change:tunnel:dimensions
 *   {tunnel_id, render:true, accessories_fetch:true}` + `changed:tunnel_<id>:sizes`
 *   (+ `changed:tunnel_<id>:width` width-only, + `get:overall:height(true)`
 *   for height) + `tunnelLugs:recalcPlacement` queue (§7.3.1 model listener)
 *   — on a hybrid AHU that last step is an AHU-wide placement re-read instead,
 *   the server having already re-laid the runs (refetchHybridLugPlacements).
 * Pre-save the cabinet fires `change:tunnel:widthOrHeight` (+ `:<id>`).
 */

interface TunnelCabinetRowProps {
  ahu: IAhuBoardUnit;
  tunnel: IAhuBoardTunnel;
  /** Every tunnel on this AHU — needed to find back-to-back/OU peers for the
   * Y-Offset visibility rule and to resolve the (row, level) anchor tunnel. */
  tunnels: IAhuBoardTunnel[];
  cms: ICmsAhuDoc | undefined;
  mfgId: number;
  /** Hybrid AHU write gate — Walls/Roof/Floor is Power-user only. */
  isPowerUser: boolean;
}

/* Short labels, not legacy's "Ups End Wall"/"Ds End Wall"/"Left Side Wall"/…
 * (tab.ahu.cabinet.tmpl): the checkbox popup and the one-line summary share
 * this list, and the long forms overflowed the 185px summary as soon as three
 * faces were on. Deliberate deviation from KA-x-user-skill. */
const FACE_FLAG_FIELDS = [
  { name: 'has_upstream_end_wall', label: 'Upstream' },
  { name: 'has_downstream_end_wall', label: 'Downstream' },
  { name: 'has_left_side_wall', label: 'Left' },
  { name: 'has_right_side_wall', label: 'Right' },
  { name: 'has_roof', label: 'Roof' },
  { name: 'has_floor', label: 'Floor' },
] as const;
type FaceFlagField = (typeof FACE_FLAG_FIELDS)[number]['name'];

const TUNNEL_OFFSET_DIRECTIONS: Array<{
  value: NonNullable<IAhuBoardTunnel['tunnel_offset_direction']>;
  label: string;
}> = [
  { value: 'left_aligned', label: 'Left Aligned' },
  { value: 'centered', label: 'Centered' },
  { value: 'right_aligned', label: 'Right Aligned' },
  { value: 'user_defined_left', label: 'User Defined Left' },
  { value: 'user_defined_right', label: 'User Defined Right' },
];

/**
 * Y-Offset visibility (§7.3.1 tab.ahu.tunnel.cabinet.js):
 *  - Hybrid: shown on every tunnel except the 1:1:1 anchor.
 *  - Non-hybrid: shown only for a back-to-back peer (position > 1 vs. the
 *    row/level's position-1 tunnel) or an OU upper tunnel (level > 1 vs.
 *    level-1, position 1) whose width differs from its peer's by > 1e-6.
 */
const showYOffset = (
  tunnel: IAhuBoardTunnel,
  tunnels: IAhuBoardTunnel[],
  isHybrid: boolean | undefined
): boolean => {
  const row = tunnel.row ?? 1;
  const level = tunnel.level ?? 1;
  const position = tunnel.position ?? 1;

  if (isHybrid) {
    return !(row === 1 && level === 1 && position === 1);
  }

  const findPeer = () => {
    if (position > 1) {
      return tunnels.find(
        (t) => (t.row ?? 1) === row && (t.level ?? 1) === level && (t.position ?? 1) === position - 1
      );
    }
    if (level > 1) {
      return tunnels.find(
        (t) => (t.row ?? 1) === row && (t.level ?? 1) === level - 1 && (t.position ?? 1) === 1
      );
    }
    return undefined;
  };
  const peer = findPeer();
  if (!peer) return false;
  const widthDiff = Math.abs(Number(tunnel.width ?? 0) - Number(peer.width ?? 0));
  return widthDiff > 1e-6;
};

const TunnelCabinetRow = ({
  ahu,
  tunnel,
  tunnels,
  cms,
  mfgId,
  isPowerUser,
}: TunnelCabinetRowProps) => {
  const dispatch = useAppDispatch();
  const [patchTunnel] = usePatchAhuTunnelMutation();
  const [patchCasing] = usePatchAhuCasingMutation();
  const [patchFloor] = usePatchAhuFloorMutation();
  const [emptyRows, setEmptyRows] = useState<Record<number, boolean>>({});
  const [tagValue, setTagValue] = useState(tunnel.custom_name ?? '');
  const [faceFlagsOpen, setFaceFlagsOpen] = useState(false);
  const [pendingFaceFlags, setPendingFaceFlags] = useState<Partial<Record<FaceFlagField, boolean>>>(
    {}
  );
  const faceFlagsRowRef = useRef<HTMLLIElement>(null);
  const faceFlagsListRef = useRef<HTMLDivElement>(null);

  // Outer-skin / skin-finish live on the CASING row, under-insulation /
  // under-liner on the FLOOR row (legacy tunnel.casing / tunnel.floor models
  // — the tunnel payload does not carry these fields).
  const { data: casing } = useGetAhuCasingQuery(
    tunnel.casing_id != null ? Number(tunnel.casing_id) : skipToken
  );
  const { data: floor } = useGetAhuFloorQuery(
    tunnel.floor_id != null ? Number(tunnel.floor_id) : skipToken
  );

  const widths = cmsWidthValues(cms);
  const heights = cmsHeightValues(cms);
  const ccid = ahu.construction_class_id ?? null;

  // W/H options are CMS value NAMES ("36.00" — value_names is "{:.2f}");
  // tunnel.width is a float (36) — match numerically so the native select's
  // selected option is correct (legacy: tunnel.width.toFixed(2) == name).
  const matchedDim = (values: Array<string | number>, raw: unknown): string => {
    const n = Number(raw);
    const hit = values.find((v) => Number(v) === n);
    return hit != null ? String(hit) : String(raw ?? '');
  };
  const widthValue = matchedDim(widths, tunnel.width);
  const heightValue = matchedDim(heights, tunnel.height);

  const changeTunnel = (name: 'width' | 'height', value: number) => {
    ahuBoardBus.emit('change:tunnel:widthOrHeight', { tunnel_id: tunnel.id, name, value });
    ahuBoardBus.emit(`change:tunnel:widthOrHeight:${tunnel.id}`);
    patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: { [name]: value } })
      .unwrap()
      .then(() => {
        // §19.2 tunnel cascade.
        void dispatch(validateTunnel(tunnel.id));
        ahuBoardBus.emit('change:tunnel:dimensions', {
          tunnel_id: tunnel.id,
          render: true,
          accessories_fetch: true,
        });
        ahuBoardBus.emit(`changed:tunnel_${tunnel.id}:sizes`);
        if (name === 'width') ahuBoardBus.emit(`changed:tunnel_${tunnel.id}:width`);
        if (name === 'height') ahuBoardBus.emit('get:overall:height', true);
        if (ahu.is_hybrid) {
          // Hybrid lugs are laid out server-side and this PATCH has already
          // re-laid them, AHU-wide — so re-read the placements rather than arm
          // ReCalculate over a canvas the arming itself just emptied. Measured
          // in refetchHybridLugPlacements.
          void dispatch(refetchHybridLugPlacements(tunnel.ahu_id, { hideWhilePending: true }));
        } else {
          ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
            tunnel_id: tunnel.id,
            hide_from_canvas: true,
          });
        }
      })
      .catch(() => undefined);
  };

  const changeCasing = (field: 'outerliner_id' | 'finish_id', id: number | null) => {
    if (tunnel.casing_id == null) return;
    patchCasing({ id: Number(tunnel.casing_id), data: { [field]: id } })
      .unwrap()
      .then(() => {
        // QA5 #7 — write the new id into the tunnel's section rows so the
        // CABINET tab selects update without a refresh (legacy
        // tab.section.js:133-147 listens on tunnel.casing and re-getValues).
        dispatch(
          ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnel.id, (rows) =>
            rows.map((row) => ({ ...row, [field]: id }))
          )
        );
        if (field === 'outerliner_id') {
          ahuBoardBus.emit('change:tunnel:casing', { tunnel_id: tunnel.id });
          // [NEG]#58 — casing.callValidate passes the CASING id as tunnel id
          // (works because casing_id == tunnel_id in practice); we validate
          // the tunnel directly.
          void dispatch(validateTunnel(tunnel.id));
        }
      })
      .catch(() => undefined);
  };

  const changeFloor = (
    field: 'under_insulation_id' | 'underliner_id',
    id: number | null
  ) => {
    if (tunnel.floor_id == null) return;
    patchFloor({
      id: Number(tunnel.floor_id),
      data: { [field]: id, tunnel_id: tunnel.id },
    })
      .unwrap()
      .then(() => {
        ahuBoardBus.emit(`update:tunnel:${Number(tunnel.floor_id)}:fields`);
        // QA5 #7 — refetch the tunnel's section rows so the CABINET tab's
        // readonly Under-Insulation/Under-Liner rows refresh without a page
        // reload (legacy `update:tunnel:<floorId>:fields` listener,
        // tab.section.js:157-164).
        dispatch(
          ahuBoardApi.util.invalidateTags([{ type: 'AhuSections', id: tunnel.id }])
        );
        if (field === 'under_insulation_id') {
          // under-insulation thickness affects canvas Y (§3.1) — refetch
          // tunnels (the legacy cabinet does model.fetch()).
          dispatch(
            ahuBoardApi.util.invalidateTags([{ type: 'AhuTunnels', id: tunnel.ahu_id }])
          );
        }
      })
      .catch(() => undefined);
  };

  const saveTag = (value: string) => {
    if (value === (tunnel.custom_name ?? '')) return;
    patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: { custom_name: value } })
      .unwrap()
      .catch(() => undefined);
  };

  const toggleFaceFlag = (field: FaceFlagField) => {
    setPendingFaceFlags((prev) => {
      const current = field in prev ? prev[field] : Boolean(tunnel[field]);
      return { ...prev, [field]: !current };
    });
  };

  // The list closes in `.finally()`, i.e. only once the PATCH has come back, so
  // it is still open and still clickable for the length of that round-trip. A
  // second click in that window used to re-send the same payload — reachable
  // before by double-clicking the icon, and easier to hit now that the whole
  // row is the activator. One PATCH per commit; `pendingFaceFlags` is cleared
  // in the same `.finally()`, so there is nothing left to send anyway.
  const faceFlagsSavingRef = useRef(false);

  const saveFaceFlags = () => {
    if (faceFlagsSavingRef.current) return;
    if (Object.keys(pendingFaceFlags).length === 0) {
      setFaceFlagsOpen(false);
      return;
    }
    faceFlagsSavingRef.current = true;
    const floorRemoved = pendingFaceFlags.has_floor === false;
    patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: pendingFaceFlags })
      .unwrap()
      .then(() => {
        ahuBoardBus.emit('change:tunnel:dimensions', { tunnel_id: tunnel.id, render: true });
        // Unchecking Floor drops the whole floor build-up server-side —
        // AHUTunnelFloor.save() clears Under-Insulation and Under-Liner once
        // the last section's floor is gone (AHUTunnelViewSet.partial_update).
        // Those two rows read `getAhuFloor`, which this PATCH does not touch,
        // so without an explicit invalidate they keep showing the removed
        // materials until the board is reloaded — and the next edit to either
        // select would PATCH the stale sibling id straight back.
        if (floorRemoved) {
          if (tunnel.floor_id != null) {
            dispatch(
              ahuBoardApi.util.invalidateTags([
                { type: 'Ahu', id: `floor-${Number(tunnel.floor_id)}` },
              ])
            );
            ahuBoardBus.emit(`update:tunnel:${Number(tunnel.floor_id)}:fields`);
          }
          // CABINET tab mirrors both as readonly per-section rows.
          dispatch(ahuBoardApi.util.invalidateTags([{ type: 'AhuSections', id: tunnel.id }]));
        }
      })
      .catch(() => undefined)
      .finally(() => {
        faceFlagsSavingRef.current = false;
        setPendingFaceFlags({});
        setFaceFlagsOpen(false);
      });
  };

  const faceFlagValue = (field: FaceFlagField): boolean =>
    field in pendingFaceFlags ? Boolean(pendingFaceFlags[field]) : Boolean(tunnel[field]);

  const checkedFaceLabels = FACE_FLAG_FIELDS.filter(({ name }) => faceFlagValue(name)).map(
    ({ label }) => label
  );
  const checkedFacesText = checkedFaceLabels.join(', ');

  // The tooltip is the ellipsis's companion, so it appears only while there IS
  // an ellipsis — `scrollWidth > clientWidth` on the (nowrap, overflow:hidden)
  // summary. Four faces fit the 185px box and want no tooltip; six do not.
  // Measured off the DOM rather than from the string length because the
  // separators are CSS `::after` content and the font is whatever the data bar
  // resolved to.
  //
  // Measured on every summary change AND again on pointer-enter. The summary
  // text is not the only thing that can move the answer: the row mounts inside
  // a collapsed `.sublist`, the web font can resolve after the first commit,
  // and browser zoom re-lays the 185px box out from under a stored `true`/
  // `false`. Any of those leaves the flag stale, and a stale `false` is exactly
  // "hovering does nothing". Re-measuring on enter costs one layout read at the
  // only moment the answer is used, and lands before the shared tooltip's 0.5s
  // reveal delay, so the bubble still appears on the first hover.
  const facesSummaryRef = useRef<HTMLSpanElement | null>(null);
  const [facesTruncated, setFacesTruncated] = useState(false);
  const measureFaces = useCallback(() => {
    const el = facesSummaryRef.current;
    const truncated = el ? el.scrollWidth > el.clientWidth : false;
    setFacesTruncated((prev) => (prev === truncated ? prev : truncated));
  }, []);
  useEffect(measureFaces, [checkedFacesText, measureFaces]);

  // Both halves of the row carry the tooltip — the caption as well as the
  // summary. "Walls, Roof & Floor" is what the row is *called*, so it is what
  // people point at when they want to see the faces the ellipsis ate; hanging
  // the tooltip on the value alone made hovering the row look broken. The
  // caption is a float, not an ancestor of the `position: absolute` face-flags
  // popup, so giving it `.ka-has-tooltip`'s `position: relative` cannot move
  // that popup the way promoting the `li`/`div` would.
  //
  // `tooltip` is a non-standard attribute, hence the spread — same trick as
  // FanCurveCanvas.tsx's zoom buttons.
  const facesTooltipClass = facesTruncated ? ' ka-has-tooltip f-size-11' : '';
  const facesTooltipAttr = facesTruncated ? { tooltip: checkedFacesText } : {};

  // The WHOLE ROW is the activator, not just the icon button: clicking the
  // "Walls, Roof & Floor" caption, the summary of checked faces, or the gap
  // between them opens the multiselect. The caption is the widest thing in the
  // row and the natural thing to aim at, so a 20px icon being the only way in
  // read as a dead row.
  //
  // One handler for the row, and the button deliberately has no onClick of its
  // own — its click bubbles to this `li`, so any click is handled exactly once
  // and one click is still one PATCH.
  //
  // Toggle, matching what the button did: open when closed, and when already
  // open COMMIT and close (legacy's `tunnel:face-flags:save`), so clicking the
  // caption a second time behaves as it did before this change.
  //
  // Clicks inside `.tunnel-face-flag-edit-list` are the exception — the six
  // checkboxes are descendants of this `li` too, and toggling a face must not
  // close the popup out from under the next one.
  const toggleFaceFlagsRow = (e: ReactMouseEvent<HTMLLIElement>) => {
    if (faceFlagsListRef.current?.contains(e.target as Node)) return;
    if (faceFlagsOpen) saveFaceFlags();
    else setFaceFlagsOpen(true);
  };

  // Clicking outside the open multiselect closes it — and COMMITS, because
  // legacy's outside-click path is `main.js` closeOpenedBoxes (bound to the
  // board's `click`), which fires `tunnel:face-flags:save` → the same
  // saveTunnelFaceFlags the activator runs. It is a commit, not a discard; a
  // no-op when nothing was toggled.
  //
  // "Outside" is now the row, not legacy's narrower
  // `#show_tunnel_face_flags`-or-inside-the-list set: everything within the
  // `li` — button, list, caption, summary — is handled by toggleFaceFlagsRow
  // above, and must stay excluded here or both paths would fire saveFaceFlags
  // on one click and send two PATCHes. The user-visible result for a click on
  // the caption or summary is unchanged (commit + close); it is now the row
  // handler doing it rather than this listener.
  useEffect(() => {
    if (!faceFlagsOpen) return undefined;
    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (faceFlagsRowRef.current?.contains(target)) return;
      saveFaceFlags();
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceFlagsOpen, pendingFaceFlags]);

  // §19.2: offset/dimension edits hide this tunnel's lugs from the canvas
  // immediately, before the redraw the PATCH success triggers, so the redraw
  // never paints them at their now-stale position (legacy model/tunnel.js
  // onChange, run BEFORE model.set()).
  const changeOffsetField = (
    name: 'x_offset' | 'tunnel_offset_direction' | 'tunnel_offset',
    value: string | number
  ) => {
    if (!ahu.is_hybrid) {
      ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
        tunnel_id: tunnel.id,
        hide_from_canvas: true,
      });
    }
    patchTunnel({ id: tunnel.id, ahuId: tunnel.ahu_id, data: { [name]: value } })
      .unwrap()
      .then(() => {
        void dispatch(validateTunnel(tunnel.id));
        ahuBoardBus.emit('change:tunnel:dimensions', { tunnel_id: tunnel.id, render: true });
        // Same fork as changeTunnel: an offset moves the tunnel's span, so the
        // server re-lays the runs across it and `tunnel_positions` reattributes
        // the lugs between tunnels. Hiding and arming ReCalculate would strand
        // a placement the PATCH had already corrected.
        if (ahu.is_hybrid) {
          void dispatch(refetchHybridLugPlacements(tunnel.ahu_id, { hideWhilePending: true }));
        }
      })
      .catch(() => undefined);
  };

  const hiddenIfEmpty = (idx: number) =>
    emptyRows[idx] ? { display: 'none' } : undefined;
  const markEmpty = (idx: number) => (empty: boolean) =>
    setEmptyRows((prev) => (prev[idx] === empty ? prev : { ...prev, [idx]: empty }));

  const isHybrid = ahu.is_hybrid;

  return (
    <>
      {/* Legacy `tab.ahu.cabinet.tmpl:1-12` on KA-x-user-skill: the row is
          `li.tunnel-custom-name-li > div.single-line`, with the input FIRST and
          the caption after it as `span.label`. Nothing styles `.tunnel-tag-li`
          or a bare `<label>` here, so the earlier markup missed every data-bar
          row rule — `.sublist > li > div > .label` (34.89% wide, floated left,
          13px, #757575) and the `li:focus-within > div > .label` blue-on-focus
          cue, which both need the `div` container and the trailing span. */}
      <li className="tunnel-custom-name-li">
        <div className="single-line">
          <input
            className="tunnel_field"
            type="text"
            name="custom_name"
            maxLength={64}
            placeholder=""
            autoComplete="off"
            value={tagValue}
            onChange={(e) => setTagValue(e.currentTarget.value)}
            onBlur={(e) => saveTag(e.currentTarget.value)}
          />
          <span className="label">Tag</span>
        </div>
      </li>
      <li className="tunnel-dimensions-li">
        <div className="multiply-total multiply multiply-tunel ">
          <span className="unit">in</span>{' '}
          <span className="selection cabinet-sizes-selection">
            {Number(tunnel.width ?? 0).toFixed(2)}
          </span>{' '}
          <select
            className="small-input tunnel_field required-field"
            name="width"
            value={widthValue}
            onChange={(e) => changeTunnel('width', Number(e.currentTarget.value))}
          >
            {widths.map((w) => (
              <option key={String(w)} value={String(w)}>
                {String(w)}{' '}
              </option>
            ))}
          </select>{' '}
          <span className="unit large"> W </span> <span className="multiply-sign">x</span>{' '}
          <span className="selection cabinet-sizes-selection">
            {Number(tunnel.height ?? 0).toFixed(2)}
          </span>{' '}
          <select
            className="small-input tunnel_field required-field"
            name="height"
            value={heightValue}
            onChange={(e) => changeTunnel('height', Number(e.currentTarget.value))}
          >
            {heights.map((h) => (
              <option key={String(h)} value={String(h)}>
                {String(h)}
              </option>
            ))}
          </select>{' '}
          <span className="unit large"> H </span> <span className="multiply-sign">x</span>{' '}
          <input
            className="small-input tunnel_field"
            type="text"
            data-type="number"
            name="length"
            data-min={tunnel.length_min ?? 12}
            data-max={tunnel.length_max ?? 600}
            data-step="0.01"
            value={formatLegacyNumber(tunnel.length, 0.01)}
            tabIndex={-1}
            readOnly
            onChange={() => undefined}
          />{' '}
          <span className="unit large"> L</span>
        </div>
      </li>
      <li data-select-index="0" style={hiddenIfEmpty(0)}>
        <div className="single-line-with-custom-select">
          <div id="outer-skin-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/casing/metal_sheets"
              constructionClassId={ccid}
              value={(casing?.outerliner_id as number | null | undefined) ?? null}
              onChange={(id) => changeCasing('outerliner_id', id)}
              selectName="outerliner_id"
              selectClass="tunnel_casing_field"
              hasNoneOption={false}
              onEmptyChange={markEmpty(0)}
            />
          </div>
          <span className="label">Outer Skin</span>
        </div>
      </li>
      <li data-select-index="1" style={hiddenIfEmpty(1)}>
        <div className="single-line-with-custom-select">
          <div id="skin-finish-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/casing/finishes"
              constructionClassId={ccid}
              value={(casing?.finish_id as number | null | undefined) ?? null}
              onChange={(id) => changeCasing('finish_id', id)}
              selectName="finish_id"
              selectClass="tunnel_casing_field"
              onEmptyChange={markEmpty(1)}
            />
          </div>
          <span className="label">
            Skin Finish
          </span>
        </div>
      </li>
      <li data-select-index="2" style={hiddenIfEmpty(2)}>
        <div className="single-line-with-custom-select">
          <div id="under-insulation-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/floor/under_insulations"
              constructionClassId={ccid}
              value={(floor?.under_insulation_id as number | null | undefined) ?? null}
              onChange={(id) => changeFloor('under_insulation_id', id)}
              selectName="under_insulation_id"
              selectClass="tunnel_floor_field"
              onEmptyChange={markEmpty(2)}
            />
          </div>
          <span className="label">Floor Under-Insulation</span>
        </div>
      </li>
      <li data-select-index="3" style={hiddenIfEmpty(3)}>
        <div className="single-line-with-custom-select">
          <div id="under-liner-select" className="select-container">
            <PaginatedSelect
              mfgId={mfgId}
              path="ahu/floor/under_liner_metal_sheets"
              constructionClassId={ccid}
              value={(floor?.underliner_id as number | null | undefined) ?? null}
              onChange={(id) => changeFloor('underliner_id', id)}
              selectName="underliner_id"
              selectClass="tunnel_floor_field"
              onEmptyChange={markEmpty(3)}
            />
          </div>
          <span className="label">Floor Under-Liner</span>
        </div>
      </li>

      {/* Markup is the legacy tab.ahu.cabinet.tmpl block verbatim: an
          `li.tunnel-face-flag-group` wrapping a bare div that holds the icon
          button, the edit list, the checked-flags summary and the label, in
          that order. Every rule this widget has in ahuBoardOverrides.scss is
          scoped under `.tunnel-face-flag-group`, so the shape has to match or
          none of them apply. The edit list stays mounted and opens via
          `.active` (legacy showTunnelFaceFlags/hideTunnelFaceFlags) — it is a
          max-height transition on a position:absolute popup, not a mount.

          The click handler is on the `li`, the row itself, so the whole row
          opens the multiselect — including any padding outside the inner div.
          `onClick` on an `li` changes no CSS (it is not `position` or a class),
          so the absolutely-positioned popup still resolves against the same
          containing block it always did. */}
      {isHybrid && isPowerUser && (
        <li
          ref={faceFlagsRowRef}
          className="tunnel-face-flag-group"
          onClick={toggleFaceFlagsRow}
        >
          {/* One listener for the whole row: React fires `onMouseEnter` on the
              div whenever the pointer crosses into its subtree from outside,
              so it covers the caption, the summary and the icon button alike —
              whichever half the user actually aims at, the measurement is
              refreshed before the tooltip is asked for. */}
          <div onMouseEnter={measureFaces}>
            <button
              id="show_tunnel_face_flags"
              type="button"
              className="icon-mounting icon-selection"
              tabIndex={-1}
            />
            <div
              ref={faceFlagsListRef}
              className={`tunnel-face-flag-edit-list${faceFlagsOpen ? ' active' : ''}`}
            >
              <ul>
                {FACE_FLAG_FIELDS.map(({ name, label }) => (
                  <li key={name}>
                    <label>
                      <input
                        type="checkbox"
                        className="tunnel-face-flag"
                        name={name}
                        checked={faceFlagValue(name)}
                        onChange={() => toggleFaceFlag(name)}
                        tabIndex={-1}
                      />
                      <span className="label">{label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            {/* One <span> per checked face, no JS separator: legacy draws the
                commas with `.checked-tunnel-face-flags span:not(:empty):not(
                :last-child):after { content: ', ' }`. Joining in JS would
                collapse them into one text node and lose the per-span
                font/colour/nowrap rules with it.

                The summary is kept to ONE line (the container is `nowrap` +
                `text-overflow: ellipsis`, see ahuBoardOverrides.scss), so six
                checked faces read as "Upstream, Downstream, Left, Right,…"
                rather than wrapping and pushing the rest of the data bar down.
                Order is FACE_FLAG_FIELDS order throughout: upstream,
                downstream, left, right, roof, floor.

                The `{' '}` after each span stays: every span is
                `white-space: nowrap` and the comma lives in the span's own
                `::after`, so without it the faces would run together as
                "Upstream,Downstream".

                The hover tooltip is the app-wide `.ka-has-tooltip` + `tooltip`
                attribute widget (common.css:31569-31634, `content:
                attr(tooltip)` on `:hover:after` — grey #8d8d8d bubble, 10px
                Roboto Medium, 0.5s delay), not a native `title`, so it matches
                every other tooltip on the board. Two things force it onto a
                WRAPPER rather than the summary span itself:

                  · `.ka-has-tooltip` is `position: relative` and the bubble is
                    its own `::after`. On the summary — `overflow: hidden`, the
                    very rule that draws the ellipsis — the bubble would be
                    clipped to the 185px box and never seen.
                  · the `li` and its `div` are out too: neither is positioned
                    today, and the face-flags popup below is `position:
                    absolute` against the data bar with a tuned `margin-left:
                    230px`/`bottom: 4px`. Making either an ancestor containing
                    block would move the popup.

                `f-size-11` is the shared 11px modifier from the same block
                (common.css:31635), not a size of our own: it is what the other
                tooltips in this Data Bar already use (InternalWallsGroup.tsx,
                DmountGroup.tsx), so the row reads at the same size as its
                neighbours rather than at the 10px base. */}
            <span
              className={`checked-tunnel-face-flags-anchor${facesTooltipClass}`}
              {...facesTooltipAttr}
            >
              <span ref={facesSummaryRef} className="checked-tunnel-face-flags output">
                {FACE_FLAG_FIELDS.filter(({ name }) => faceFlagValue(name)).map(
                  ({ name, label }) => (
                    <Fragment key={name}>
                      <span>{label}</span>{' '}
                    </Fragment>
                  )
                )}
              </span>
            </span>
            <span className={`label${facesTooltipClass}`} {...facesTooltipAttr}>
              Walls, Roof &amp; Floor
            </span>
          </div>
        </li>
      )}

      {/* X-Offset and Y-Offset follow Walls/Roof/Floor, and both rows are
          `li.tunnel-offset-li` — the order and the class names are
          tab.ahu.cabinet.tmpl:144-186 on KA-x-user-skill verbatim.

          The markup shape is load-bearing twice over. Neither row has a rule
          of its own anywhere in that branch's stylesheets (grepped: the only
          `.tunnel-offset-li` occurrences are the two `<li>`s); they are drawn
          entirely by the generic data-bar row rules, all of which key off
          `.sublist > li > div > …`. The earlier `li > label > input` shape
          matched none of them, so both rows fell back to browser defaults —
          no floated 34.89%-wide caption, no `in` unit, and on the Y row a
          native-looking <select> box, because the rule that makes every other
          dropdown in this tab render its text transparent behind a companion
          `.selection` span is `.inputs-group .sublist > li div select` and
          needs the `div`.

          Verified those generic rules are byte-identical between the served
          legacy sheet and KA-x-user-skill's, so matching the markup is
          sufficient — there is no CSS to port for these two rows. */}
      {isHybrid && (
        <li className="tunnel-offset-li">
          <div>
            <span className="unit">in</span>
            <input
              className="tunnel_field medium-input"
              type="text"
              data-type="number"
              data-formatter_type="decimal"
              data-step="0.01"
              name="x_offset"
              id={`x_offset_${tunnel.id}`}
              /* `x_offset` is not only typed here — reversing a hybrid
                 tunnel's airflow direction re-bases it server-side
                 (`airflowFlipPatch`, because the stored number names the
                 UPSTREAM edge and flipping changes which edge that is). The
                 row is keyed on `tunnel.id` and never remounts, and
                 `defaultValue` is applied at mount only, so the field kept
                 showing the pre-flip number until the board was reloaded —
                 and blurring it then PATCHed that stale number back, moving
                 the tunnel by its own length.

                 Legacy has no equivalent gap: `tab.ahu.tunnel.cabinet.js:293-316`
                 re-renders the whole cabinet template on ANY model change, so
                 the save inside `toggle_airflow_direction` repaints this input
                 with the new value. Keying the input on the stored value is
                 the same thing scoped to the one field — it remounts exactly
                 when legacy would have repainted, and (also as in legacy) a
                 FAILED save writes nothing, leaves the key alone and keeps
                 whatever the user typed. */
              key={formatLegacyNumber(tunnel.x_offset ?? 0, 0.01)}
              defaultValue={formatLegacyNumber(tunnel.x_offset ?? 0, 0.01)}
              onBlur={(e) => {
                const value = Number(e.currentTarget.value);
                if (Number.isFinite(value)) changeOffsetField('x_offset', value);
              }}
            />
            <span className="label">X-Offset (+/-)</span>
          </div>
        </li>
      )}

      {showYOffset(tunnel, tunnels, isHybrid) && (
        <li className="tunnel-offset-li">
          <div className="single-line">
            {/* §annotateTunnelOffsetState — the <select>'s own text is
                transparent, so the resolved label is what the user reads. */}
            <span className="selection">
              {TUNNEL_OFFSET_DIRECTIONS.find(
                (o) => o.value === (tunnel.tunnel_offset_direction ?? 'centered')
              )?.label ?? tunnel.tunnel_offset_direction}
            </span>
            <select
              className="tunnel_field"
              name="tunnel_offset_direction"
              style={{ width: '136px' }}
              id={`tunnel_offset_direction_${tunnel.id}`}
              value={tunnel.tunnel_offset_direction ?? 'centered'}
              onChange={(e) =>
                changeOffsetField('tunnel_offset_direction', e.currentTarget.value)
              }
            >
              {TUNNEL_OFFSET_DIRECTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="unit">in</span>
            {/* Legacy always renders this input and only DISABLES it outside
                the two user_defined_* directions ({{#unless
                tunnel.tunnel_offset_user_defined}}disabled{{/unless}}).
                Unmounting it instead, as this did, left the row a different
                height and width from legacy's. The `key` reproduces the
                re-render legacy gets for free: Backbone re-templates the whole
                view on every save, so the box always reloads the stored value
                when the direction changes. */}
            <input
              key={tunnel.tunnel_offset_direction ?? 'centered'}
              className="tunnel_field small-input"
              type="text"
              data-type="number"
              data-formatter_type="decimal"
              data-step="0.01"
              data-min="0"
              name="tunnel_offset"
              id={`tunnel_offset_${tunnel.id}`}
              defaultValue={formatLegacyNumber(tunnel.tunnel_offset ?? 0, 0.01)}
              disabled={
                tunnel.tunnel_offset_direction !== 'user_defined_left' &&
                tunnel.tunnel_offset_direction !== 'user_defined_right'
              }
              onBlur={(e) => {
                const value = Number(e.currentTarget.value);
                if (Number.isFinite(value) && value >= 0) {
                  changeOffsetField('tunnel_offset', value);
                }
              }}
            />
            <span className="label">Y Offset</span>
          </div>
        </li>
      )}
    </>
  );
};

export default TunnelCabinetRow;
